import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HiddenAnnotationsToast } from '../components/HiddenAnnotationsToast';
import { IAnnotationsStore } from '../modules/annotation/annotations.store';
import { IAnnotationUiStore } from '../modules/annotation/annotationUi.store';
import { IReviewImagesQueueService } from '../modules/annotation/reviewImagesQueue.service';
import { IAnnotationTypeBl } from '../modules/annotation/submodules/annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from '../modules/annotation/submodules/annotationTypes/annotationType.store';

export const HiddenAnnotationsToastContainer = observer((): ReactElement => {
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const imagesQueueService = useInjection(IReviewImagesQueueService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const { projectId } = useParams<{ projectId: string }>();

  const [showAnnotationsHiddenToast, setShowAnnotationsHiddenToast] = useState(false);
  const [imageId, setImageId] = useState('');

  const shouldRender = annotationsStore.image !== undefined || annotationUiStore.isImageLoading || imagesQueueService.isAnyRequestInProgress;

  const showToast = (): void => {
    setShowAnnotationsHiddenToast(true);
  };

  const hideToast = (): void => {
    setShowAnnotationsHiddenToast(false);
  };

  const unhideAllAnnotationTypes = (): void => {
    annotationTypeBl.unhideAllAnnotationTypeOptions(projectId);
    annotationsStore.setHiddenSegmentations([]);
    hideToast();
  };

  const anySegmentationIsHidden = (): boolean => {
    const hiddenMarkingToolsIds = annotationTypeStore.hiddenAnnotationTypes.find((hat) => hat.projectId === projectId)?.annotationTypes;

    const existsHiddenSegmentation =
      annotationsStore.hiddenSegmentations.filter((hs) =>
        annotationsStore.segmentations.some((s) => (hs.segmentationId !== undefined && hs.segmentationId === s.id) || hs.featureId === s.feature.id)
      ).length > 0;

    const existsSegmentationWithHiddenTool = annotationsStore.segmentations.some((s) =>
      hiddenMarkingToolsIds?.some((id) => id === s.feature.properties?.annotationTypeId)
    );

    return existsHiddenSegmentation || existsSegmentationWithHiddenTool;
  };

  useEffect(() => {
    setImageId(annotationsStore.image?.id || '');

    if (anySegmentationIsHidden()) {
      showToast();
    }
  }, []);

  useEffect(() => {
    if (annotationsStore.image !== undefined && imageId !== annotationsStore.image.id) {
      setImageId(annotationsStore.image.id);

      if (anySegmentationIsHidden()) {
        showToast();
      } else {
        hideToast();
      }
    }
  }, [annotationsStore.image]);

  return shouldRender && showAnnotationsHiddenToast ? (
    <HiddenAnnotationsToast
      hideToast={hideToast}
      unhideAllAnnotationTypes={unhideAllAnnotationTypes}
    />
  ) : (
    <></>
  );
});
